/**
 * currencyService — CRUD for school-level currencies.
 *
 * Currencies are units of value (points, merits, money, stars) that can be
 * awarded by domain types. They live at schools/{schoolId}/currencies.
 *
 * Each currency can optionally groupBy a student field (e.g. "house")
 * to enable leaderboard-style aggregations.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'currencies';

function currenciesRef(schoolId) {
  return collection(db, 'schools', schoolId, COLLECTION);
}

/**
 * Derive ledger collection names from a currency's machine name.
 * @param {string} name — currency machine name (e.g. "positive_points")
 * @returns {{ transactions: string, balances: string, groups: string }}
 */
export function deriveLedgerCollections(name) {
  const safe = name.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return {
    transactions: `ledger_${safe}_transactions`,
    balances: `ledger_${safe}_balances`,
    groups: `ledger_${safe}_groups`,
  };
}

/**
 * Create a new currency for a school.
 * @param {string} schoolId
 * @param {{ name: string, label: string, unit?: string, unitPlural?: string, groupBy?: string|null }} data
 * @returns {Promise<string>} The new currency document ID
 */
export async function createCurrency(schoolId, data) {
  if (!schoolId) throw new Error('createCurrency: schoolId is required');
  if (!data.name) throw new Error('createCurrency: name is required');
  if (!data.label) throw new Error('createCurrency: label is required');

  const safeName = data.name.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  const docRef = await addDoc(currenciesRef(schoolId), {
    name: safeName,
    label: data.label,
    unit: data.unit || 'point',
    unitPlural: data.unitPlural || 'points',
    groupBy: data.groupBy || null,
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Get all active currencies for a school.
 * @param {string} schoolId
 * @returns {Promise<Array>}
 */
export async function getCurrencies(schoolId) {
  if (!schoolId) return [];

  const q = query(currenciesRef(schoolId), where('active', '==', true));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get active currencies that aggregate by a group key.
 * Built-in event domains use this only as a safe compatibility fallback when
 * exactly one house currency exists for the school.
 *
 * @param {string} schoolId
 * @param {string} groupBy
 * @returns {Promise<Array>}
 */
export async function getCurrenciesByGroup(schoolId, groupBy) {
  if (!schoolId || !groupBy) return [];

  const q = query(
    currenciesRef(schoolId),
    where('active', '==', true),
    where('groupBy', '==', groupBy)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get a single currency by ID.
 * @param {string} schoolId
 * @param {string} currencyId
 * @returns {Promise<Object|null>}
 */
export async function getCurrency(schoolId, currencyId) {
  if (!schoolId || !currencyId) return null;

  const ref = doc(db, 'schools', schoolId, COLLECTION, currencyId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Subscribe to currencies in real-time.
 * @param {string} schoolId
 * @param {Function} callback — receives array of currency docs
 * @returns {Function} unsubscribe
 */
export function subscribeCurrencies(schoolId, callback) {
  if (!schoolId) {
    callback([]);
    return () => {};
  }

  const q = query(currenciesRef(schoolId), where('active', '==', true));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

/**
 * Subscribe to the first active currency that aggregates by a given group key
 * (e.g. `groupBy: 'house'`). Used by leaderboard widgets that need to bind to
 * "the school's house-points currency" without hard-coding a specific id.
 *
 * @param {string} schoolId
 * @param {string} groupBy — student field used for aggregation (e.g. "house")
 * @param {Function} callback — receives the currency doc or null
 * @returns {Function} unsubscribe
 */
export function subscribeCurrencyByGroup(schoolId, groupBy, callback) {
  if (!schoolId || !groupBy) {
    callback(null);
    return () => {};
  }

  const q = query(
    currenciesRef(schoolId),
    where('active', '==', true),
    where('groupBy', '==', groupBy)
  );
  return onSnapshot(q, (snap) => {
    if (snap.empty) {
      callback(null);
      return;
    }
    const doc = snap.docs[0];
    callback({ id: doc.id, ...doc.data() });
  });
}

/**
 * Update a currency.
 * @param {string} schoolId
 * @param {string} currencyId
 * @param {Object} updates
 */
export async function updateCurrency(schoolId, currencyId, updates) {
  if (!schoolId || !currencyId) throw new Error('updateCurrency: schoolId and currencyId required');

  const ref = doc(db, 'schools', schoolId, COLLECTION, currencyId);
  await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
}

/**
 * Soft-delete a currency (set active: false).
 * @param {string} schoolId
 * @param {string} currencyId
 */
export async function deactivateCurrency(schoolId, currencyId) {
  return updateCurrency(schoolId, currencyId, { active: false });
}
